// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.0.4
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import api from '../api/client'
import {
  CreditCard,
  Key,
  Download,
  MessageSquare,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react'

interface DashboardData {
  subscription: {
    plan_name: string
    status: string
    current_period_end: string
  } | null
  licenses: {
    total: number
    active: number
  }
  usage: {
    users: number
    endpoints: number
    vulnerabilities: number
    max_users: number
    max_endpoints: number
    max_vulnerabilities: number
  }
  tickets: {
    open: number
    total: number
  }
}

export default function Dashboard() {
  const { user } = useAuth()
  const [data, setData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadDashboard()
  }, [])

  const loadDashboard = async () => {
    try {
      const [subRes, licRes, usageRes, ticketRes] = await Promise.all([
        api.get('/api/portal/billing/subscription').catch(() => ({ data: null })),
        api.get('/api/portal/licenses').catch(() => ({ data: [] })),
        api.get('/api/portal/account/usage').catch(() => ({ data: { usage: {}, limits: {} } })),
        api.get('/api/portal/support/stats').catch(() => ({ data: { open: 0, total: 0 } }))
      ])

      setData({
        subscription: subRes.data,
        licenses: {
          total: licRes.data.length,
          active: licRes.data.filter((l: any) => l.is_active).length
        },
        usage: {
          users: usageRes.data.usage?.users || 0,
          endpoints: usageRes.data.usage?.endpoints || 0,
          vulnerabilities: usageRes.data.usage?.vulnerabilities || 0,
          max_users: usageRes.data.limits?.max_users || 0,
          max_endpoints: usageRes.data.limits?.max_endpoints || 0,
          max_vulnerabilities: usageRes.data.limits?.max_vulnerabilities || 0
        },
        tickets: ticketRes.data
      })
    } catch (error) {
      console.error('Failed to load dashboard:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  const quickLinks = [
    { icon: CreditCard, label: 'Manage Billing', path: '/billing', color: 'text-blue-500' },
    { icon: Key, label: 'View Licenses', path: '/licenses', color: 'text-green-500' },
    { icon: Download, label: 'Downloads', path: '/downloads', color: 'text-purple-500' },
    { icon: MessageSquare, label: 'Get Support', path: '/support', color: 'text-orange-500' },
  ]

  return (
    <div className="space-y-6 fade-in">
      {/* Welcome */}
      <div className="card">
        <h1 className="text-2xl font-bold mb-2">
          Welcome back, {user?.contact_name || user?.email?.split('@')[0]}!
        </h1>
        <p className="text-gray-400">
          {user?.company_name && `${user.company_name} - `}
          Here's an overview of your account.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Subscription Status */}
        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-400">Subscription</p>
              <p className="text-xl font-semibold mt-1">
                {data?.subscription?.plan_name || 'No Plan'}
              </p>
            </div>
            {data?.subscription?.status === 'active' ? (
              <CheckCircle className="w-8 h-8 text-green-500" />
            ) : (
              <AlertCircle className="w-8 h-8 text-yellow-500" />
            )}
          </div>
          {data?.subscription?.current_period_end && (
            <p className="text-xs text-gray-500 mt-2">
              Renews: {new Date(data.subscription.current_period_end).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Active Licenses */}
        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-400">Active Licenses</p>
              <p className="text-xl font-semibold mt-1">
                {data?.licenses.active || 0} / {data?.licenses.total || 0}
              </p>
            </div>
            <Key className="w-8 h-8 text-green-500" />
          </div>
        </div>

        {/* Usage */}
        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-400">Endpoints Used</p>
              <p className="text-xl font-semibold mt-1">
                {data?.usage.endpoints || 0} / {data?.usage.max_endpoints || '∞'}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
          {data?.usage.max_endpoints && (
            <div className="mt-2 h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full"
                style={{ width: `${Math.min(100, (data.usage.endpoints / data.usage.max_endpoints) * 100)}%` }}
              />
            </div>
          )}
        </div>

        {/* Support Tickets */}
        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-400">Active Tickets</p>
              <p className="text-xl font-semibold mt-1">
                {data?.tickets.open || 0}
              </p>
            </div>
            <Clock className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="flex flex-col items-center gap-3 p-4 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors"
            >
              <link.icon className={`w-8 h-8 ${link.color}`} />
              <span className="text-sm font-medium">{link.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Usage Details */}
      {data?.subscription && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Usage Overview</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">Users</span>
                <span>{data.usage.users} / {data.usage.max_users}</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full"
                  style={{ width: `${Math.min(100, (data.usage.users / data.usage.max_users) * 100)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">Endpoints</span>
                <span>{data.usage.endpoints} / {data.usage.max_endpoints}</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full"
                  style={{ width: `${Math.min(100, (data.usage.endpoints / data.usage.max_endpoints) * 100)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">Vulnerabilities</span>
                <span>{data.usage.vulnerabilities.toLocaleString()} / {data.usage.max_vulnerabilities.toLocaleString()}</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500 rounded-full"
                  style={{ width: `${Math.min(100, (data.usage.vulnerabilities / data.usage.max_vulnerabilities) * 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
